
import React from "react";
import { useTranslations } from "use-intl";
import { 
  DropdownMenu, 
  DropdownMenuCheckboxItem, 
  DropdownMenuContent, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useBankCatalog } from "@/hooks/useBankCatalog";
import { formatBankAccountLabel } from "@/utils/bankAccountLabel";
import type { BankAccountSummary } from "@/types/accounts";

type FilterAccount = Pick<BankAccountSummary, "id" | "bankName" | "bic" | "maskedNumber" | "chartAccountNumber">;

interface BankAccountMultiSelectProps {
  accounts: FilterAccount[];
  selectedAccounts: string[];
  setSelectedAccounts: (accounts: string[]) => void;
  /** The account list failed to load: say so, instead of looking like a company with no accounts. */
  loadFailed?: boolean;
}

const BankAccountMultiSelect = ({
  accounts,
  selectedAccounts,
  setSelectedAccounts,
  loadFailed = false,
}: BankAccountMultiSelectProps) => {
  const t = useTranslations("consultantBank.accountFilter");
  const { getBankDisplayName } = useBankCatalog();
  const allSelected = selectedAccounts.length === 0;

  // Same one-line label as the transaction table's Account column (FIN-802), never the raw
  // accountNumber, which can hold the full IBAN.
  const labelFor = (account: FilterAccount) =>
    formatBankAccountLabel({
      bankDisplayName: getBankDisplayName(account.bic, account.bankName),
      maskedNumber: account.maskedNumber,
      chartAccountNumber: account.chartAccountNumber,
    }).full;
  
  const handleSelectAll = () => {
    setSelectedAccounts([]);
  };
  
  const handleToggleAccount = (accountId: string) => {
    setSelectedAccounts(
      selectedAccounts.includes(accountId)
        ? selectedAccounts.filter(id => id !== accountId)
        : [...selectedAccounts, accountId]
    );
  };

  const getButtonText = () => {
    if (allSelected) {
      return t("all");
    } else if (selectedAccounts.length === 1) {
      const selectedAccount = accounts.find(a => a.id === selectedAccounts[0]);
      return selectedAccount ? labelFor(selectedAccount) : t("count", { count: 1 });
    } else {
      return t("count", { count: selectedAccounts.length });
    }
  };

  const renderAccountOptions = () => {
    if (loadFailed) {
      return (
        <p role="alert" className="px-2 py-1.5 text-sm text-destructive">
          {t("loadFailed")}
        </p>
      );
    }
    if (accounts.length === 0) {
      return <p className="px-2 py-1.5 text-sm text-muted-foreground">{t("none")}</p>;
    }
    return accounts.map(account => (
      <DropdownMenuCheckboxItem
        key={account.id}
        checked={selectedAccounts.includes(account.id)}
        onCheckedChange={() => handleToggleAccount(account.id)}
      >
        {labelFor(account)}
      </DropdownMenuCheckboxItem>
    ));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full md:w-auto">
          {getButtonText()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuLabel>{t("label")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuCheckboxItem
          checked={allSelected}
          onCheckedChange={handleSelectAll}
        >
          {t("all")}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {renderAccountOptions()}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BankAccountMultiSelect;
